import {
  OpenFeature,
  TypedInMemoryProvider,
  Provider,
  EvaluationContext,
  InMemoryFlagConfiguration,
  JsonValue,
} from '@openfeature/web-sdk';
import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import buildEvaluationContext, { FeatureFlagSubject } from '@shared/src/lib/feature-flag-context';
import { RemoteProviderName } from '@shared/src/lib/feature-flag-providers';
import { FLAGS } from '@shared/src/lib/feature-flag-keys';

/**
 * Feature-flag bootstrap for both Vue apps (TL-3952).
 *
 * `VITE_FEATURE_FLAG_PROVIDER` picks the provider here and nowhere else: `local` (in-memory and the
 * default, so dev and CI need no remote service — R8) or `flagsmith` (needs an environment ID).
 * Flagsmith won the TL-3950 evaluation; keep the driver names aligned with lms-service.
 *
 * Adding or swapping a provider is one entry in REMOTE_PROVIDERS, with no call-site changes (R10).
 * A misconfigured, unreachable or slow provider falls back to local instead of breaking boot (R6).
 */

/** Re-exported so `FLAGS` can be imported from here or from the keys module — see its docblock. */
export { FLAGS } from '@shared/src/lib/feature-flag-keys';

export type FeatureFlagProviderMode = RemoteProviderName | 'local';

/** Max time to wait on a remote provider before giving up on it and using the local one. */
const PROVIDER_INIT_TIMEOUT_MS = 5000;

/**
 * How long boot may wait for flags. Well below PROVIDER_INIT_TIMEOUT_MS so a slow provider cannot
 * hold the app hostage; registration finishes in the background and `useFeatureFlag` catches up.
 */
const BOOT_BUDGET_MS = 1500;

/**
 * Local-provider values. Mirror `feature-flags.static.flags` in lms-service so both tiers behave
 * the same offline. The example flag serves `on` to prove the wiring in dev and CI (R8); the
 * safe-default guarantee still lives at the call site, not here (R6).
 */
// `as const` keeps `defaultVariant` a literal so TypedInMemoryProvider can check it against
// `variants` — without it the type widens to string and the config goes unvalidated.
const LOCAL_FLAG_CONFIGURATION = {
  [FLAGS.platformExample]: {
    disabled: false,
    variants: { on: true, off: false },
    defaultVariant: 'on',
  },
  [FLAGS.assignmentGroupEnabled]: {
    disabled: false,
    variants: { on: true, off: false },
    defaultVariant: 'off',
  },
  [FLAGS.courseCatalogAdminView]: {
    disabled: false,
    variants: { on: true, off: false },
    defaultVariant: 'off',
  },
  [FLAGS.airmasonHandbooks]: {
    disabled: false,
    variants: { on: true, off: false },
    defaultVariant: 'off',
  },
} as const;

/**
 * `buildEvaluationContext` builds one vendor-neutral context; each provider reshapes it here, since
 * providers disagree on where attributes live — Flagsmith reads them ONLY from a nested `traits`
 * object and silently ignores flat ones. Keeping the translation here is what lets the
 * targeting-key policy stay identical across tiers.
 */
type ContextAdapter = (context: EvaluationContext) => EvaluationContext;

const passThroughContext: ContextAdapter = (context) => context;

const toFlagsmithContext: ContextAdapter = ({ targetingKey, ...attributes }) => ({
  ...(targetingKey === undefined ? {} : { targetingKey }),
  traits: attributes,
});

/**
 * Each provider is paired with the credential it cannot work without, so a half-filled .env
 * degrades to local instead of failing at boot. Env is read lazily to keep this testable.
 */
const REMOTE_PROVIDERS: Record<RemoteProviderName, {
  credential: () => string | undefined;
  adaptContext: ContextAdapter;
  create: (context: EvaluationContext) => Provider;
}> = {
  flagsmith: {
    credential: () => import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID,
    adaptContext: toFlagsmithContext,
    // Flagsmith fetches on init and on identity change only. Without `realtime` (SSE) a console
    // toggle reaches an open tab on the next re-identify or reload — the R4 kill-switch caveat.
    create: () => new FlagsmithClientProvider({
      environmentID: import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID,
      api: import.meta.env.VITE_FLAGSMITH_API_URL || undefined,
      realtime: import.meta.env.VITE_FLAGSMITH_REALTIME === 'true',
    }),
  },
};

/** Rejects after `ms`, so a slow provider can never hold up app boot or the auth flow. */
const rejectAfter = (ms: number, message: string): Promise<never> => new Promise((_resolve, reject) => {
  setTimeout(() => reject(new Error(message)), ms);
});

let resolvedMode: FeatureFlagProviderMode = 'local';

/** Reshapes the canonical context for whichever provider actually registered. */
let activeAdapter: ContextAdapter = passThroughContext;

/**
 * The provider actually in use, which differs from the requested one when remote init failed. The
 * fallback is silent for users, so this is how you tell a real remote evaluation from a local one.
 */
export const getFeatureFlagProviderMode = (): FeatureFlagProviderMode => resolvedMode;

export interface FeatureFlagDegradation {
  /** The provider `VITE_FEATURE_FLAG_PROVIDER` asked for. */
  requested: FeatureFlagProviderMode;
  /** What we ended up using — always `local` today. */
  resolved: FeatureFlagProviderMode;
  /** Missing credential, timeout, or the error the provider threw. */
  reason: string;
}

let degradationListener: ((degradation: FeatureFlagDegradation) => void) | null = null;

/**
 * Called when a requested provider could not be used, so every flag is now serving its call-site
 * default — otherwise indistinguishable from every flag being switched off (PLAT-41 §8.5). Each app
 * wires this to whatever reporting it has; one listener, and a second call replaces the first.
 */
export const onFeatureFlagDegraded = (
  listener: (degradation: FeatureFlagDegradation) => void,
): void => {
  degradationListener = listener;
};

/** Best-effort: the listener is app-supplied, and a reporter with a dead transport must not stop boot. */
const reportDegradation = (requested: FeatureFlagProviderMode, reason: string): void => {
  try {
    degradationListener?.({ requested, resolved: 'local', reason });
  } catch {
    // Nowhere left to report the failure to report.
  }
};

export const featureFlagClient = OpenFeature.getClient();

/**
 * A one-shot boolean read, for the places a composable cannot go — navigation guards, route meta,
 * plain functions. Components must use `useFeatureFlag` instead: this returns a value rather than a
 * reactive one, so a flag that resolves later will not re-render anything that read it here.
 *
 * Evaluation is synchronous against the provider's local cache and never throws: an unregistered,
 * erroring or mid-reconciliation provider yields the caller's default, same contract as R6.
 */
export const getBooleanFlag = (flagKey: string, defaultValue: boolean): boolean => {
  try {
    return featureFlagClient.getBooleanValue(flagKey, defaultValue);
  } catch {
    return defaultValue;
  }
};

/** Unknown values fall back to `local` rather than disabling flags outright. */
const requestedMode = (): FeatureFlagProviderMode => {
  const requested = String(import.meta.env.VITE_FEATURE_FLAG_PROVIDER || '');

  return requested in REMOTE_PROVIDERS ? (requested as FeatureFlagProviderMode) : 'local';
};

/**
 * Dev-time ergonomics. The alternative to saying something is a variable that silently does
 * nothing, which is the exact confusion the override mechanism exists to remove.
 */
// eslint-disable-next-line no-console
const warnAboutOverrides = (message: string): void => console.warn(`Feature flags: ${message}`);

/**
 * `VITE_FEATURE_FLAG_OVERRIDES` as a flag-key → value map, so flipping a flag in dev is a `.env`
 * edit rather than an edit to LOCAL_FLAG_CONFIGURATION, which is tracked and shared with everyone.
 *
 *   VITE_FEATURE_FLAG_OVERRIDES={"platform_example_flag":false}
 *
 * Anything that is not a JSON object is ignored and reported, never thrown: this runs in front of a
 * top-level await in both apps, and a typo in a convenience variable must not blank the page. Vite
 * reads .env at startup only, so a change here needs a dev-server restart, not a reload.
 */
const parseLocalOverrides = (): Record<string, JsonValue> => {
  const raw = import.meta.env.VITE_FEATURE_FLAG_OVERRIDES;

  if (!raw) return {};

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    warnAboutOverrides(`VITE_FEATURE_FLAG_OVERRIDES is not valid JSON and was ignored: ${raw}`);
    return {};
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    warnAboutOverrides(
      'VITE_FEATURE_FLAG_OVERRIDES must be a JSON object, e.g. {"platform_example_flag":false}. Ignored.',
    );
    return {};
  }

  return parsed as Record<string, JsonValue>;
};

/** One override value in the single-variant shape TypedInMemoryProvider expects. */
const asOverriddenFlag = (value: JsonValue) => ({
  disabled: false,
  variants: { override: value },
  defaultVariant: 'override',
});

/**
 * The in-memory provider, with any `.env` overrides applied over LOCAL_FLAG_CONFIGURATION.
 *
 * A key that is not declared in LOCAL_FLAG_CONFIGURATION is **added**, not ignored — that mirrors
 * lms-service's static overrides, and it is the only way to exercise a flag before it is declared.
 * The cost is that a typo looks like a working override, so unknown keys are called out.
 */
const localProvider = (): Provider => {
  const overrides = parseLocalOverrides();
  const overridden = Object.keys(overrides);

  // The no-override path is the common one — and the only one where TypedInMemoryProvider can still
  // check `defaultVariant` against `variants` off the `as const` literal. Merging widens the type
  // and loses that check, so it happens only when a developer has explicitly asked for it.
  if (!overridden.length) return new TypedInMemoryProvider(LOCAL_FLAG_CONFIGURATION);

  const undeclared = overridden.filter((key) => !(key in LOCAL_FLAG_CONFIGURATION));

  if (undeclared.length) {
    warnAboutOverrides(
      `VITE_FEATURE_FLAG_OVERRIDES names ${undeclared.join(', ')}, not declared in `
      + 'LOCAL_FLAG_CONFIGURATION. Added anyway — check for a typo if a flag looks unaffected.',
    );
  }

  const merged: InMemoryFlagConfiguration = { ...LOCAL_FLAG_CONFIGURATION };

  overridden.forEach((key) => {
    merged[key] = asOverriddenFlag(overrides[key]);
  });

  return new TypedInMemoryProvider(merged);
};

const useLocalProvider = async (context: EvaluationContext): Promise<void> => {
  await OpenFeature.setProviderAndWait(localProvider(), context);
  activeAdapter = passThroughContext;
  resolvedMode = 'local';
};

async function registerProvider(subject: FeatureFlagSubject): Promise<FeatureFlagProviderMode> {
  const context = buildEvaluationContext(subject);
  const mode = requestedMode();
  const remote = REMOTE_PROVIDERS[mode];

  if (remote) {
    if (!remote.credential()) {
      reportDegradation(mode, `No credential configured for the "${mode}" feature-flag provider`);
    } else {
      try {
        await Promise.race([
          OpenFeature.setProviderAndWait(remote.create(context), remote.adaptContext(context)),
          rejectAfter(PROVIDER_INIT_TIMEOUT_MS, `The "${mode}" feature-flag provider timed out`),
        ]);

        activeAdapter = remote.adaptContext;
        resolvedMode = mode;

        return resolvedMode;
      } catch (error) {
        // Fall through to the local provider — a flag backend must never break app boot.
        reportDegradation(mode, error instanceof Error ? error.message : String(error));
      }
    }
  }

  try {
    await useLocalProvider(context);
  } catch (error) {
    // Nothing left to fall back to: no provider is registered and every call site serves its own
    // default, which is the contract (R6). Rejecting would leave main.ts's await hanging, unmounted.
    reportDegradation(mode, error instanceof Error ? error.message : String(error));
  }

  return resolvedMode;
}

/**
 * The registration in flight, so a later context update can wait for the provider that actually ends
 * up active instead of guessing. Resolved by default: with no provider registered, `passThroughContext`
 * is already the right adapter.
 */
let registration: Promise<FeatureFlagProviderMode> = Promise.resolve('local');

/**
 * Registers a provider and installs the initial evaluation context. Never rejects: any failure
 * resolves to the local provider. Returns the mode actually in use.
 */
export function initFeatureFlags(subject: FeatureFlagSubject = {}): Promise<FeatureFlagProviderMode> {
  registration = registerProvider(subject);

  return registration;
}

/**
 * What both apps call before mounting: gives a healthy provider a short window so the first render
 * sees real values, then continues regardless. Never rejects, and never delays boot past
 * BOOT_BUDGET_MS.
 */
export async function initFeatureFlagsBeforeMount(subject: FeatureFlagSubject = {}): Promise<void> {
  await Promise.race([
    initFeatureFlags(subject),
    new Promise<void>((resolve) => {
      setTimeout(resolve, BOOT_BUDGET_MS);
    }),
  ]);
}

/**
 * Re-targets evaluation once the signed-in subject is known, so rollouts bucket on the real tenant
 * rather than nothing. Safe to await from the auth flow: never rejects and time-boxed, so a broken
 * flag backend cannot stall login. Pass no subject on logout to drop the targeting key.
 */
export async function updateFeatureFlagContext(subject: FeatureFlagSubject = {}): Promise<void> {
  // Boot is capped at BOOT_BUDGET_MS while registration may run to PROVIDER_INIT_TIMEOUT_MS, so an
  // auth flow can land here while `activeAdapter` is still the pre-registration default. Applying the
  // context then would shape it for the wrong provider — Flagsmith reads traits only from a nested
  // object and silently ignores flat ones, so every trait would be dropped and nothing would repair
  // it until the next tenant switch. Waiting for the registration is what keeps the shape correct.
  try {
    await Promise.race([
      registration,
      rejectAfter(PROVIDER_INIT_TIMEOUT_MS, 'Feature-flag provider registration timed out'),
    ]);
  } catch {
    // Registration never rejects, so this is the timeout. Apply the context anyway: the targeting key
    // is top-level in every shape, so a partial update still beats dropping the re-target entirely.
  }

  try {
    await Promise.race([
      OpenFeature.setContext(activeAdapter(buildEvaluationContext(subject))),
      rejectAfter(PROVIDER_INIT_TIMEOUT_MS, 'Feature-flag context update timed out'),
    ]);
  } catch {
    // Keep evaluating against the previous context rather than surfacing a flag error to the user.
  }
}
